import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { Language } from '@repo/ui/lang'
import { LanguageSelector } from '@repo/ui/language-selector'
import { Separator } from '@repo/ui/separator'
import {
  Globe,
  OctagonAlert,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
} from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks.ts'
import { CaseWorker, Organization } from '@/store/user'
import { customValidationPasswords, meSchema, userPasswordSchema } from '@/validation-schemas'

export function MeEditor({
  caseWorker,
  modalHandler,
  organization,
}: {
  caseWorker: CaseWorker
  modalHandler: { open: boolean; setOpen: (isOpen: boolean) => void }
  organization: Organization
}) {
  const { t } = useT({ keyPrefix: 'settings' })

  return (
    <Dialog
      defaultOpen={modalHandler.open}
      open={modalHandler.open}
      onOpenChange={modalHandler.setOpen}
    >
      <DialogContent size="wide">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Settings className="mr-2" />
            {t('title')}
          </DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <div className="grid py-4">
          <LanguageEditor language={caseWorker?.language} organization={organization} />
          <Separator className="my-6" />

          <CaseWorkerEditor caseWorker={caseWorker} shouldResetForm={modalHandler.open} />
          <Separator className="my-6" />

          <PasswordEditor shouldResetForm={modalHandler.open} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
function CaseWorkerEditor({
  caseWorker,
  shouldResetForm,
}: {
  caseWorker: CaseWorker
  shouldResetForm: boolean
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const caseWorkerFormDefaultValues = useMemo(
    () => ({
      firstName: caseWorker.firstName,
      lastName: caseWorker.lastName,
    }),
    [caseWorker],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues: caseWorkerFormDefaultValues,
    onSubmit: (values) => {
      void dispatch(
        storeActions.caseWorker.updateCurrent({
          firstName: values.firstName,
          lastName: values.lastName,
        }),
      )
    },
    schema: meSchema,
  })

  useEffect(() => {
    form.reset(caseWorkerFormDefaultValues)
  }, [shouldResetForm])

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-4" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('firstName')}</FormLabel>
              <FormControl>
                <Input placeholder={tc('firstName')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('lastName')}</FormLabel>
              <FormControl>
                <Input placeholder={tc('lastName')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel>{tc('email')}</FormLabel>
          <FormControl>
            <Input disabled type="email" placeholder={tc('email')} value={caseWorker.email} />
          </FormControl>
          <FormMessage />
        </FormItem>
        <Button type="submit" disabled={!isFormValid}>
          <UserCheck className="size-4" />
          {tc('update', { cfl: true })}
        </Button>
      </form>
    </Form>
  )
}

function LanguageEditor({
  language,
  organization,
}: {
  language?: Language | undefined
  organization: Organization
}) {
  const dispatch = useStoreDispatch()
  const { tc, setLanguage } = useT()
  const defaultLanguage = language || organization.language || Language.EN

  return (
    <div className="flex items-center gap-2">
      <Globe className="text-primary-600 size-4" />
      <Label>{tc('languagePreference')}</Label>
      <LanguageSelector
        defaultLanguage={defaultLanguage}
        onSetLanguage={(newLanguage) => {
          void setLanguage(
            newLanguage,
            () =>
              void dispatch(
                storeActions.caseWorker.updateCurrent({
                  language: newLanguage,
                }),
              ),
          )
        }}
      />
    </div>
  )
}

function PasswordEditor(shouldResetForm: { shouldResetForm: boolean }) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const passwordFormDefaultValues = useMemo(
    () => ({
      password: '',
      confirmPassword: '',
    }),
    [],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues: passwordFormDefaultValues,
    onSubmit: (values) => {
      void dispatch(
        storeActions.user.updatePassword({
          password: values.password,
        }),
      )
      form.reset(passwordFormDefaultValues)
    },
    schema: userPasswordSchema,
    customValidation: (values) => {
      return customValidationPasswords(values.password, values.confirmPassword)
    },
  })

  useEffect(() => {
    form.reset(passwordFormDefaultValues)
  }, [shouldResetForm])

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-4" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-flex items-center">
                {field.value.length === 0 ? (
                  <Shield className="mr-1 size-4" />
                ) : field.value.length < 12 ? (
                  <ShieldAlert className="mr-1 size-4 text-red-500" />
                ) : (
                  <ShieldCheck className="mr-1 size-4 text-green-500" />
                )}
                {tc('password')}
              </FormLabel>
              <FormControl>
                <Input type="password" placeholder={tc('password')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('password')}</FormLabel>
              <FormControl>
                <Input type="password" placeholder={tc('password')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={!isFormValid}>
          <OctagonAlert className="size-5" />
          {tc('update', { cfl: true })}
        </Button>
      </form>
    </Form>
  )
}
